import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { toast } from "sonner";

// Source-reliability labels approved by Management (Stage B-UI).
// Any unlisted or missing value resolves to the "unknown" label.
const RELIABILITY_LABELS = {
  staff_verified:    "Previously verified by your tax team",
  client_confirmed:  "Previously confirmed by you",
  ai_generated:      "From your profile — please verify",
  verify_recommended:"From your profile — please verify",
  unknown:           "From your profile",
};

function PrefillBadge({ qKey, reliability }) {
  const label = RELIABILITY_LABELS[reliability] ?? RELIABILITY_LABELS.unknown;
  const isVerified = reliability === "staff_verified";
  const isClientConfirmed = reliability === "client_confirmed";
  const needsReview = reliability === "ai_generated" || reliability === "verify_recommended";
  const colorCls = isVerified
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : isClientConfirmed
    ? "text-blue-700 bg-blue-50 border-blue-200"
    : needsReview
    ? "text-amber-700 bg-amber-50 border-amber-200"
    : "text-slate-600 bg-slate-50 border-slate-200";
  return (
    <span data-testid={`porg-prefill-${qKey}`}
      className={`ml-2 inline-flex items-center gap-1 text-[11px] font-medium border rounded-full px-2 py-0.5 align-middle ${colorCls}`}>
      <Icons.Sparkles className="h-3 w-3" />{label}
    </span>
  );
}

function HistoricalNote({ qKey, historicalValue, taxYear }) {
  const valueLabel = historicalValue === true ? "Yes" : historicalValue === false ? "No" : null;
  if (valueLabel === null) return null;
  const yearPhrase = taxYear ? String(taxYear) : "the current tax year";
  return (
    <p data-testid={`porg-history-${qKey}`}
      className="mt-2 text-xs text-slate-500 italic border-l-2 border-slate-200 pl-2">
      Our records previously indicated: <strong>{valueLabel}</strong>.
      Please confirm whether this applies to {yearPhrase}.
    </p>
  );
}

function Question({ q, value, onChange, onDna, disabled, flagged, taxYear }) {
  const id = `porg-q-${q.key}`;
  const dna = q.answer_status === "does_not_apply";
  const opts = (q.options || []).map((o) => (typeof o === "object" ? o : { value: o, label: String(o).replace(/_/g, " ") }));
  const ctx = q.prefill_context || null;
  // Show the prefill badge only when the server confirms this is still a profile_prefill answer.
  // If the client has subsequently corrected the answer, entry_method changes to
  // "client_self_service" and ctx.prefill_source_type will be null / missing.
  const showPrefillBadge = q.entry_method === "profile_prefill" && !dna && ctx?.prefill_source_type === "profile_prefill";
  // Show historical context note for annual activity indicators (question is unanswered).
  const showHistoryNote = !dna && ctx?.prefill_source_type === "profile_history";
  return (
    <div id={`porg-anchor-${q.key}`} data-testid={`porg-question-${q.key}`}
      className={`py-4 border-b border-slate-100 last:border-0 ${flagged ? "bg-rose-50/60 -mx-6 px-6 rounded-lg" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <label htmlFor={id} className="block text-sm font-medium text-[#171B2E]">
          {q.label}{q.required && <span className="text-rose-500 ml-0.5">*</span>}
          {flagged && <span className="ml-2 text-[11px] font-semibold text-rose-600" data-testid={`porg-flag-${q.key}`}>Needs attention</span>}
          {showPrefillBadge && (
            <PrefillBadge qKey={q.key} reliability={ctx.source_reliability} />
          )}
        </label>
        {q.allow_dna !== false && (
          <button type="button" onClick={() => onDna(!dna)} disabled={disabled}
            data-testid={`porg-q-${q.key}-dna`}
            className={`shrink-0 text-[11px] font-medium rounded-full px-2.5 py-1 border transition-colors ${dna ? "bg-slate-700 text-white border-slate-700" : "border-slate-200 text-slate-500 hover:border-slate-400"} disabled:opacity-50`}>
            {dna ? "✓ Does not apply" : "Does not apply"}
          </button>
        )}
      </div>
      {dna ? (
        <p className="mt-2 text-xs text-slate-400 italic">Marked as not applicable to your situation.</p>
      ) : (
        <div className="mt-2.5">
          {q.type === "boolean" || q.type === "yes_no" ? (
            <div className="flex gap-2">
              {[["Yes", true], ["No", false]].map(([lbl, v]) => (
                <button key={lbl} type="button" disabled={disabled} data-testid={`porg-q-${q.key}-${lbl.toLowerCase()}`} onClick={() => onChange(v)}
                  className={`text-sm px-5 py-2 rounded-lg border transition-colors disabled:opacity-50 ${value === v ? "bg-[#171B2E] text-white border-[#171B2E]" : "border-slate-200 text-slate-600 hover:border-[#F5A623]"}`}>{lbl}</button>
              ))}
            </div>
          ) : q.type === "select" ? (
            <select id={id} data-testid={id} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}
              className="w-full sm:w-80 border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 disabled:opacity-60">
              <option value="">Select…</option>
              {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : q.type === "number" || q.type === "currency" ? (
            <input id={id} data-testid={id} type="number" value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
              className="w-full sm:w-80 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 disabled:opacity-60" />
          ) : q.type === "date" ? (
            <input id={id} data-testid={id} type="date" value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}
              className="w-full sm:w-80 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 disabled:opacity-60" />
          ) : (
            <input id={id} data-testid={id} type="text" value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}
              className="w-full sm:w-80 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 disabled:opacity-60" />
          )}
          {showHistoryNote && (
            <HistoricalNote qKey={q.key} historicalValue={ctx.historical_value} taxYear={taxYear} />
          )}
        </div>
      )}
    </div>
  );
}

export default function PortalOrganizer() {
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");
  const [submitErrors, setSubmitErrors] = useState([]);

  const load = useCallback(() => portalApi.get("/portal/organizer").then((r) => { setData(r.data); setDraft({}); }).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);

  const org = data?.organizer;
  const state = data?.state;
  const validation = data?.validation;
  const editable = org?.editable;
  const dirty = Object.keys(draft).length > 0;
  const getVal = (q) => (q.key in draft ? draft[q.key] : q.answer);
  const flaggedKeys = new Set(submitErrors.map((e) => e.question_id));

  const scrollTo = (key) => {
    const el = document.getElementById(`porg-anchor-${key}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const save = async () => {
    if (!dirty) return true;
    setBusy("save");
    try { await portalApi.post("/portal/organizer/answers", { answers: draft }); toast.success("Saved"); await load(); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save."); return false; }
    finally { setBusy(""); }
  };

  const setDna = async (q, enabled) => {
    setBusy("dna");
    try { await portalApi.post("/portal/organizer/does-not-apply", { question_key: q.key, enabled }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update."); }
    finally { setBusy(""); }
  };

  const submit = async () => {
    setBusy("submit");
    setSubmitErrors([]);
    try {
      if (dirty) { await portalApi.post("/portal/organizer/answers", { answers: draft }); }
      const r = await portalApi.post("/portal/organizer/submit");
      const out = r.data?.document_summary?.outstanding || 0;
      toast.success(out > 0 ? `Organizer submitted — ${out} document(s) still needed.` : "Organizer submitted — thank you!");
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.unresolved_questions)) {
        setSubmitErrors(detail.unresolved_questions);
        toast.error(`${detail.unresolved_question_count} item(s) need attention before submitting.`);
        if (detail.unresolved_questions[0]) scrollTo(detail.unresolved_questions[0].question_id);
      } else {
        toast.error(typeof detail === "string" ? detail : "Please complete all required questions.");
      }
      await load();
    } finally { setBusy(""); }
  };

  const requestReopen = async () => {
    setBusy("reopen");
    try { await portalApi.post("/portal/organizer/request-reopen", { reason: "" }); toast.success("Request sent — your tax team will be in touch."); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send request."); }
    finally { setBusy(""); }
  };

  if (data === undefined) return <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>;

  const docSummary = org?.document_summary;

  return (
    <div className="max-w-3xl mx-auto" data-testid="portal-organizer">
      <button onClick={() => navigate("/portal")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#171B2E] mb-4"><Icons.ArrowLeft className="h-4 w-4" />Back to dashboard</button>
      {!org ? (
        <div className="text-center py-16 text-slate-500"><Icons.ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-3" /><p className="text-sm">Your organizer will appear here once your engagement is active.</p></div>
      ) : (
        <div className="space-y-4">
          <div className="bg-[#171B2E] text-white px-6 py-5 rounded-2xl">
            <h1 className="font-heading text-2xl font-bold">{org.name}</h1>
            <div className="mt-3 flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-[#F5A623] transition-all" style={{ width: `${state.progress}%` }} /></div>
              <span className="text-sm text-[#F5A623] font-semibold" data-testid="porg-progress">{state.progress}%</span>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              {org.submitted ? `Submitted (v${org.submitted_version || 1}) — your team will review it shortly.` : `${state.required_remaining} required question(s) remaining`}
            </p>
          </div>

          {/* Submitted / locked confirmation */}
          {org.submitted && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="porg-submitted-panel">
              <div className="flex items-start gap-3">
                <Icons.ClipboardCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-emerald-900 text-sm">Organizer submitted</div>
                  <p className="text-sm text-emerald-700 mt-0.5">Thank you — your answers are locked while your team reviews them.</p>
                  {org.reopen_requested ? (
                    <p className="text-xs text-emerald-700 mt-2 font-medium" data-testid="porg-reopen-requested">✓ Reopen requested — your tax team will follow up.</p>
                  ) : (
                    <button onClick={requestReopen} disabled={busy === "reopen"} data-testid="porg-request-reopen-btn"
                      className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 hover:text-emerald-900 border border-emerald-300 rounded-lg px-3 py-1.5">
                      <Icons.Unlock className="h-3.5 w-3.5" />Need to change something? Request reopen
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Document outstanding summary (never blocks submission) */}
          {docSummary && docSummary.outstanding > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4" data-testid="porg-documents-summary">
              <div className="flex items-start gap-3">
                <Icons.FileUp className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-blue-900 text-sm" data-testid="porg-documents-outstanding-count">
                    {docSummary.received} of {docSummary.total} required documents received
                  </div>
                  <p className="text-sm text-blue-700 mt-0.5">{docSummary.outstanding} document(s) still needed. You can upload these any time — they don't block your organizer.</p>
                  <ul className="mt-2 space-y-1">
                    {docSummary.outstanding_documents.slice(0, 6).map((d) => (
                      <li key={d.document_request_id} className="text-xs text-blue-800 flex items-center gap-1.5"><Icons.Dot className="h-3 w-3" />{d.title}</li>
                    ))}
                  </ul>
                  <button onClick={() => navigate("/portal/documents")} data-testid="porg-goto-documents-btn"
                    className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-800 hover:text-blue-900">
                    <Icons.ArrowRight className="h-3.5 w-3.5" />Upload documents
                  </button>
                </div>
              </div>
            </div>
          )}

          {org.status === "waiting_on_client" && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-sm text-amber-800 flex items-start gap-2" data-testid="porg-clarification">
              <Icons.MessageCircleQuestion className="h-4 w-4 mt-0.5 shrink-0" />Your tax team has asked you to review or update some answers below.
            </div>
          )}

          {org.status === "reopened" && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-sm text-amber-800 flex items-start gap-2" data-testid="porg-reopened-banner">
              <Icons.Unlock className="h-4 w-4 mt-0.5 shrink-0" />Your organizer was reopened for updates. Please review your answers and resubmit.
            </div>
          )}

          {(() => {
            const allQs = state.sections.flatMap((s) => s.questions);
            const hasPrefill = allQs.some((q) => q.entry_method === "profile_prefill");
            const hasHistory = allQs.some((q) => q.prefill_context?.prefill_source_type === "profile_history");
            const hasStaffVerified = allQs.some((q) => q.prefill_context?.source_reliability === "staff_verified");
            if (!org.submitted && (hasPrefill || hasHistory)) {
              const bannerText = hasStaffVerified
                ? "Your tax team has verified some of your information from previous records. Please review and confirm."
                : "Some information was filled in from your Tax Flow profile. Please review and update anything that has changed.";
              return (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 text-sm text-emerald-800 flex items-start gap-2" data-testid="porg-prefill-banner">
                  <Icons.Sparkles className="h-4 w-4 mt-0.5 shrink-0" />{bannerText}
                </div>
              );
            }
            return null;
          })()}

          {/* Actionable submit-failure summary */}
          {submitErrors.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4" data-testid="porg-submit-errors">
              <div className="flex items-center gap-2 mb-2">
                <Icons.AlertTriangle className="h-5 w-5 text-rose-600" />
                <div className="font-semibold text-rose-900 text-sm">Organizer not ready — {submitErrors.length} item(s) need attention</div>
              </div>
              <ul className="space-y-1.5">
                {submitErrors.map((e) => (
                  <li key={e.question_id}>
                    <button onClick={() => scrollTo(e.question_id)} data-testid={`porg-error-jump-${e.question_id}`}
                      className="text-left text-sm text-rose-800 hover:text-rose-900 hover:underline flex items-center gap-1.5">
                      <Icons.ArrowRight className="h-3.5 w-3.5 shrink-0" /><span className="text-slate-500">{e.section_label}:</span> {e.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {state.sections.map((sec) => sec.questions.length > 0 && (
            <div key={sec.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`porg-section-${sec.key}`}>
              <div className="px-6 py-3.5 border-b border-slate-100 bg-slate-50/60"><h2 className="font-semibold text-[#171B2E] text-sm">{sec.label}</h2></div>
              <div className="px-6">
                {sec.questions.map((q) => (
                  <Question key={q.key} q={q} value={getVal(q)} disabled={!editable || !!busy} flagged={flaggedKeys.has(q.key)}
                    taxYear={org.tax_year}
                    onChange={(v) => setDraft((d) => ({ ...d, [q.key]: v }))} onDna={(en) => setDna(q, en)} />
                ))}
              </div>
            </div>
          ))}

          {editable && (
            <div className="sticky bottom-4 bg-white rounded-2xl border border-slate-200 shadow-lg p-3 flex items-center gap-2" data-testid="porg-actions">
              <button onClick={save} disabled={!dirty || busy === "save"} data-testid="porg-save-btn"
                className="inline-flex items-center gap-1.5 text-sm border border-slate-200 text-[#171B2E] hover:bg-slate-50 disabled:opacity-50 font-medium rounded-lg px-4 py-2.5">
                {busy === "save" ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Save className="h-4 w-4" />}Save progress
              </button>
              <button onClick={submit} disabled={busy === "submit"} data-testid="porg-submit-btn"
                className="inline-flex items-center gap-1.5 text-sm bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-50 text-[#171B2E] font-semibold rounded-lg px-5 py-2.5 ml-auto">
                {busy === "submit" ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Send className="h-4 w-4" />}Submit organizer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
